// translations/pages/kyveil/it.js

export const kyveilPageIT = {
  "seo": {
    "title": "Anonimizzazione PII con Kyveil | pAIoneer Labs GmbH",
    "description": "Kyveil rileva e anonimizza i dati personali in locale, sui tuoi sistemi. Applicazione desktop o API REST per un'anonimizzazione delle PII conforme al GDPR.",
    "keywords": "Kyveil, anonimizzazione PII, anonimizzare dati personali, protezione dei dati, privacy, GDPR, Microsoft Presidio, spaCy, flair, elaborazione locale dei dati",
    "ogTitle": "Kyveil: anonimizzazione professionale delle PII con IA e NLP",
    "ogDescription": "Rilevamento e anonimizzazione automatici dei dati personali, come applicazione desktop o API REST sulla tua infrastruttura.",
    "twitterTitle": "Anonimizzazione PII con Kyveil",
    "twitterDescription": "Rilevamento e anonimizzazione locali delle PII per documenti sensibili.",
    "url": "https://paioneer-labs.eu/kyveil",
    "structuredData": {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Kyveil",
      "applicationCategory": "SecurityApplication",
      "operatingSystem": "Windows, macOS, Linux, Server",
      "description": "Software per il rilevamento e l'anonimizzazione in locale dei dati personali con IA e NLP.",
      "provider": {
        "@type": "Organization",
        "name": "pAIoneer Labs GmbH",
        "url": "https://paioneer-labs.eu"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "url": "https://paioneer-labs.eu/kyveil"
      }
    }
  },
  "hero": {
    "eyebrow": "Protezione dei dati e anonimizzazione",
    "title": "Kyveil",
    "subtitle": "Anonimizzazione locale prima di IA, test e condivisione.",
    "description": "Kyveil è un livello di anonimizzazione locale per documenti e flussi di lavoro IA. Il software rileva i dati personali nei file Word, Excel, PDF e di testo, li sostituisce o li maschera e mostra un’anteprima prima che i contenuti entrino in chat IA, test, analisi o condivisioni esterne. Kyveil viene sviluppato e costantemente evoluto in modo [guidato dall’IDD](idd) (Intent-Driven Development) e automatizzato con [Veridion](veridion) dai nostri collaboratori IA con le persone nel loop.",
    "platforms": "Applicazione desktop, API REST e AnonChat",
    "proofPoints": [
      "I documenti restano in locale sui tuoi sistemi.",
      "I dati personali vengono sostituiti o mascherati.",
      "Un'anteprima mostra il contenuto anonimizzato prima della condivisione o della chat IA.",
      "Word, Excel, PDF e testo sono i flussi documentali principali."
    ]
  },
  "dataSovereignty": {
    "title": "I tuoi dati. I tuoi sistemi. Il tuo controllo.",
    "subtitle": "Piena sovranità sui dati grazie all'elaborazione locale.",
    "items": [
      {
        "title": "Nessun cloud necessario",
        "description": "L'intera elaborazione avviene in locale sulla tua infrastruttura. I tuoi file non lasciano mai i tuoi sistemi. Nessuna dipendenza dal cloud, nessuna trasmissione di dati all'esterno."
      },
      {
        "title": "Edge computing",
        "description": "L'applicazione desktop funziona in modo completamente autonomo sulle postazioni di lavoro. L'API viene eseguita sui tuoi server. Entrambe le versioni elaborano tutto in locale, all'interno del tuo perimetro di sicurezza."
      },
      {
        "title": "Autonomia dei dati",
        "description": "Pieno controllo sui tuoi flussi di dati. Nessun vendor lock-in. Nessun costo cloud ricorrente. Le tue informazioni sensibili restano esclusivamente sotto il tuo controllo."
      },
      {
        "title": "Hardware standard",
        "description": "Nessuna GPU necessaria. Funziona su comuni computer da ufficio e su server standard. Non servono investimenti in hardware specializzato."
      }
    ]
  },
  "features": {
    "title": "Funzionalità principali",
    "subtitle": "Tutto ciò che serve per rilevare e anonimizzare le PII in modo controllato nei processi aziendali.",
    "items": [
      {
        "title": "Rilevamento delle PII basato su IA",
        "description": "Riconoscimento automatico con Microsoft Presidio SDK, spaCy di Explosion AI e flair della Humboldt-Universität zu Berlin.",
        "tone": "primary"
      },
      {
        "title": "Modalità Replace",
        "description": "Sostituisce le PII rilevate con segnaposto generici come [NAME], [EMAIL] e [ADDRESS] per gli scenari di conformità.",
        "tone": "info"
      },
      {
        "title": "Modalità Fake",
        "description": "Sostituisce le PII rilevate con valori sostitutivi plausibili per ambienti di test e sviluppo.",
        "tone": "warning"
      },
      {
        "title": "Più formati di documento",
        "description": "Elabora file Microsoft Word, Excel, OpenDocument e PDF, oltre al testo semplice.",
        "tone": "success"
      },
      {
        "title": "Supporto multilingue",
        "description": "Supporto integrato per tedesco, inglese, francese, italiano e spagnolo, con modelli NLP specifici per ogni lingua.",
        "tone": "secondary"
      },
      {
        "title": "Rilevamento configurabile",
        "description": "Definisci quali entità PII rilevare: nomi, indirizzi, e-mail, numeri di telefono, conti bancari, codici fiscali e altro ancora.",
        "tone": "primary"
      },
      {
        "title": "Estensibilità",
        "description": "Interfacce per logiche personalizzate di pre-elaborazione e post-elaborazione, da integrare nei tuoi flussi di lavoro specifici.",
        "tone": "info"
      },
      {
        "title": "Ottimizzato per CPU",
        "description": "L'architettura efficiente dei modelli funziona su normale hardware da ufficio, senza bisogno di GPU.",
        "tone": "warning"
      }
    ]
  },
  "technology": {
    "title": "Stack tecnologico",
    "subtitle": "Framework di livello enterprise e ricerca NLP consolidata.",
    "items": [
      {
        "title": "Microsoft Presidio SDK",
        "description": "Microsoft Data Protection and De-identification SDK. Framework standard di settore per il riconoscimento e l'anonimizzazione delle PII, con riconoscitori di entità integrati.",
        "tone": "primary"
      },
      {
        "title": "spaCy",
        "subtitle": "Sviluppato da Explosion AI",
        "description": "Industrial-Strength Natural Language Processing. Libreria NLP pronta per la produzione, con modelli statistici preaddestrati per il riconoscimento delle entità nominate (named entity recognition).",
        "tone": "info"
      },
      {
        "title": "flair",
        "subtitle": "Sviluppato dalla Humboldt-Universität zu Berlin",
        "description": "Framework NLP allo stato dell'arte con embedding contestuali di stringhe. Modelli avanzati di reti neurali per una maggiore accuratezza nel rilevamento delle entità.",
        "tone": "secondary"
      }
    ],
    "integration": "Pipeline IA integrata",
    "integrationDesc": "Riconoscimento combinato, con Presidio come livello di orchestrazione e con i modelli spaCy e flair, per un rilevamento ottimale in più lingue e per più tipi di entità."
  },
  "technicalDetails": {
    "title": "Capacità tecniche",
    "subtitle": "Rilevamento configurabile per documenti aziendali europei.",
    "items": [
      {
        "title": "Supporto linguistico",
        "description": "Modelli NLP preaddestrati per tedesco, inglese, francese, italiano e spagnolo. Riconoscimento delle entità specifico per ogni lingua, per un rilevamento accurato delle PII nei contesti aziendali europei.",
        "tone": "primary"
      },
      {
        "title": "Entità PII configurabili",
        "description": "Scegli quali tipi di dati personali rilevare e anonimizzare: nomi di persona, indirizzi e-mail, numeri di telefono, indirizzi postali, conti bancari IBAN, numeri di carta di credito, numeri di previdenza sociale, codici fiscali, numeri di passaporto e altro ancora.",
        "tone": "info"
      },
      {
        "title": "Modalità di anonimizzazione",
        "description": "La modalità Replace sostituisce le PII con segnaposto generici per la documentazione di conformità. La modalità Fake usa valori sostitutivi plausibili per gli ambienti di test, mantenendo la struttura del documento.",
        "tone": "warning"
      },
      {
        "title": "Pre- e post-elaborazione",
        "description": "Implementa una logica personalizzata prima e dopo l'anonimizzazione. Aggiungi la validazione dei documenti, la conversione di formato, i controlli di qualità, la registrazione di audit o l'integrazione con sistemi a valle tramite le interfacce fornite.",
        "tone": "secondary"
      }
    ]
  },
  "anonChat": {
    "title": "AnonChat estende Kyveil con una chat web IA",
    "subtitle": "La nuova app applica lo stesso approccio di anonimizzazione prima della chat vera e propria con sistemi di IA esterni o interni.",
    "eyebrow": "Nuova applicazione",
    "name": "AnonChat",
    "description": "AnonChat è una chat web IA in cui i documenti Word ed Excel possono essere anonimizzati prima dell'uso con ChatGPT, Claude, Gemini o LLM interni. Un'anteprima mostra il testo anonimizzato prima che i contenuti vengano trasferiti nella chat.",
    "statement": "La decisione resta all'utente: verificare, approvare e solo dopo chattare.",
    "steps": [
      {
        "title": "Caricare il documento",
        "description": "I file Word o Excel vengono preparati per l'anonimizzazione prima di entrare nel flusso di lavoro IA.",
        "tone": "primary"
      },
      {
        "title": "Anonimizzare le PII",
        "description": "Kyveil rileva i dati personali e li sostituisce o li maschera secondo le regole scelte.",
        "tone": "info"
      },
      {
        "title": "Verificare l'anteprima",
        "description": "Il testo anonimizzato è visibile prima che i contenuti vengano passati alla chat.",
        "tone": "warning"
      },
      {
        "title": "Usare la chat IA",
        "description": "Solo dopo la verifica i contenuti anonimizzati vengono usati con LLM esterni o interni.",
        "tone": "success"
      }
    ]
  },
  "deployment": {
    "title": "Opzioni di deployment",
    "subtitle": "Scegli la soluzione adatta al tuo caso d'uso.",
    "options": [
      {
        "title": "Applicazione desktop",
        "subtitle": "Applicazione standalone per singole postazioni di lavoro",
        "description": "La versione desktop funziona in modo completamente indipendente su Windows, macOS o Linux. Ideale per chi deve anonimizzare documenti direttamente sulla propria postazione, senza dipendere dalla rete.",
        "features": [
          "Funzionamento 100% offline: nessuna connessione a Internet necessaria",
          "I file non lasciano mai la postazione di lavoro locale",
          "Elaborazione in batch di più file",
          "Interfaccia grafica per un uso semplice",
          "Funziona su normali computer da ufficio senza GPU",
          "Licenza una tantum: nessun abbonamento"
        ]
      },
      {
        "title": "API REST",
        "subtitle": "Deployment su server per integrazione e scalabilità",
        "description": "La versione API viene eseguita sui tuoi server o su un'infrastruttura di cloud privato. Ideale per integrare l'anonimizzazione in sistemi e flussi di lavoro esistenti con elevati volumi di elaborazione.",
        "features": [
          "Deployment sulla tua infrastruttura: on-premises o cloud privato",
          "Scalabilità orizzontale per requisiti di throughput elevati",
          "API RESTful con documentazione completa",
          "Stesso motore di anonimizzazione della versione desktop",
          "Esempi di integrazione per le piattaforme più diffuse",
          "Supporto per la personalizzazione di pre- e post-elaborazione"
        ]
      },
      {
        "title": "AnonChat",
        "subtitle": "Chat web IA con anonimizzazione a monte",
        "description": "AnonChat colloca Kyveil a monte della chat IA vera e propria. I documenti Word ed Excel vengono anonimizzati, un'anteprima viene verificata e solo dopo i contenuti vengono usati con ChatGPT, Claude, Gemini o LLM interni.",
        "features": [
          "Anonimizzazione prima della chat invece di un controllo a posteriori",
          "Anteprima del testo anonimizzato prima del passaggio alla chat",
          "Pensato per domande ai sistemi di IA basate su documenti",
          "Riduce il rischio di inviare dati personali a modelli esterni",
          "Collegabile a LLM interni o a configurazioni di chat aziendali",
          "Usa lo stesso approccio di anonimizzazione di desktop e API REST"
        ]
      }
    ],
    "bothNote": "Applicazione desktop, API REST e AnonChat sono progettati per anonimizzare i contenuti sensibili prima di ogni ulteriore elaborazione."
  },
  "useCases": {
    "title": "Casi d'uso",
    "subtitle": "Dalla conformità al GDPR a dati di sviluppo sicuri.",
    "items": [
      {
        "title": "Conformità al GDPR",
        "description": "Anonimizza i dati personali prima di condividere documenti all'esterno o con altri reparti interni. Soddisfa i requisiti di protezione dei dati per dati di test, dataset di analisi e documentazione.",
        "tone": "primary"
      },
      {
        "title": "Preparazione dei dati di test",
        "description": "Prepara dataset di test realistici, così che gli sviluppatori possano lavorare con dati simili a quelli di produzione senza esporre informazioni personali reali.",
        "tone": "info"
      },
      {
        "title": "Elaborazione di documenti HR",
        "description": "Anonimizza candidature, fascicoli del personale e documentazione HR per valutazioni imparziali, audit di conformità o analisi dei dati, tutelando la riservatezza.",
        "tone": "warning"
      },
      {
        "title": "Analisi dei dati",
        "description": "Rendi possibile l'analisi di documenti contenenti PII anonimizzando prima le informazioni sensibili. Condividi i dataset tra i team senza timori per la privacy.",
        "tone": "success"
      },
      {
        "title": "Archiviazione dei documenti",
        "description": "Prepara i documenti per l'archiviazione a lungo termine rimuovendo o anonimizzando le PII secondo le politiche di conservazione e le norme sulla protezione dei dati.",
        "tone": "secondary"
      },
      {
        "title": "Condivisione sicura",
        "description": "Condividi documenti con partner esterni, fornitori o altri reparti dopo la rimozione automatica delle informazioni personali sensibili.",
        "tone": "error"
      }
    ]
  },
  "releases": {
    "title": "Versioni e supporto",
    "subtitle": "Aggiornamenti, estensioni e supporto per l'uso in produzione.",
    "items": [
      {
        "title": "Desktop, API e AnonChat",
        "description": "Kyveil è disponibile come applicazione desktop e come API REST per le integrazioni. AnonChat è la chat web con anonimizzazione dei documenti a monte.",
        "tone": "info"
      },
      {
        "title": "Supporto",
        "description": "Per l'uso in azienda offriamo supporto tecnico, assistenza nell'implementazione e personalizzazioni.",
        "tone": "success"
      }
    ]
  },
  "cta": {
    "title": "Inizia con Kyveil",
    "subtitle": "Proteggi i documenti sensibili prima che finiscano in IA, test, analisi o condivisioni.",
    "desktop": "Richiedi l'applicazione desktop",
    "desktopSubject": "Kyveil Desktop",
    "apiContact": "Contatto per il deployment dell'API REST",
    "contact": "Per una demo o un preventivo, scrivici.",
    "email": "info@paioneer.eu",
    "apiSubject": "Kyveil REST API Deployment",
    "contactSubject": "Demo o preventivo per Kyveil"
  }
};
